<template>
    <Spinner v-if="displaySpinner" />
    <div class="title-and-controls">
        <EditableTitle v-model="domain" icon="pi pi-cloud" />
        <div class="controls">
            <Button
                v-if="description && !description.content && isEditing"
                v-tooltip.bottom="{
                    value: 'Will generate a first draft of the description based on the context.',
                    showDelay: 100,
                }"
                severity="secondary"
                icon="pi pi-sparkles"
                label="Generate first draft"
                @click="generateDomainDescription"
            />
        </div>
    </div>
    <section class="description" :class="{ 'edit': isEditing }">
        <TextAppArtefact
            v-if="description"
            v-model="description"
            :isEditing="isEditing"
        />
    </section>

    <section :class="{ 'edit': isEditing }">
        <ProductsList :isEditing="isEditing" :products="products" />
    </section>

    <section :class="{ 'edit': isEditing }">
        <EntitiesList :isEditing="isEditing" />
    </section>

    <section :class="{ 'edit': isEditing }">
        <RolesList :isEditing="isEditing" :roles="roles" />
    </section>

    <section :class="{ 'edit': isEditing }">
        <Documents
            v-if="domain"
            entityType="domain"
            :entity="domain"
            :isEditing="isEditing"
            :artefacts="artefacts"
        />
    </section>
</template>

<script setup lang="ts">
import Documents from "@/components/Documents.vue";
import EntitiesList from "@/components/entities/EntitiesList.vue";
import ProductsList from "@/components/Products/ProductsList.vue";
import RolesList from "@/components/Roles/RolesList.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import { useDomainManager } from "@/composables/domain-manager";
import { useArtefactsManager } from "@/lib/artefacts/user";
import Spinner from "@/shared/Spinner.vue";
import { computed } from "vue";
import { useProductsManager } from "@/stores/products-store";
import { useEditManager } from "@/composables/edit-manager";
import { useRolesManager } from "@/stores/roles-store";
import { EntityType } from "@/stores/entity-store";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";

const { artefacts } = useArtefactsManager();
const { products } = useProductsManager();
const { roles } = useRolesManager();
const { isEditing } = useEditManager();
const { domain, isFetching, description } = useDomainManager();
const { generateContent } = useGenerateDescriptionDialog();

const displaySpinner = computed(() => isFetching.value);

const generateDomainDescription = async () => {
    if (!domain.value) {
        return;
    }
    await generateContent(
        domain.value.identity,
        EntityType.DOMAIN,
        description.value?.updateContent.bind(description.value)
    );
};
</script>

<style scoped lang="scss">
.controls {
    white-space: nowrap;
}

section {
    border: 1px solid transparent;
    padding: 1rem;

    &.description {
        padding-top: 0;
        z-index: 3;
    }

    &.edit {
        border-color: var(--stekz-outline);
        border-radius: 0.5rem;

        &.description {
            padding: 0;
        }
    }
}
</style>
